import { SubscriptionDetailsResponse } from "@9amhealth/openapi";
import { Blac, Cubit } from "blac-next";
import { Props } from "components/WithAuth/WithAuth";
import reportErrorSentry from "src/lib/reportErrorSentry";
import { PayerId } from "src/constants/payers";
import AppAccessCubit, {
  AppAccessStatus
} from "src/state/AppAccessCubit/AppAccessCubit";
import {
  authenticationState,
  subscriptionState,
  userState
} from "src/state/state";
import { AuthenticationStatus } from "src/state/UserCubit/AuthenticationBloc";
import { AuthStage } from "src/types/auth";

export enum View {
  Loading = "loading",
  Locked = "locked",
  Reauth = "reauth",
  Login = "login",
  Error = "error",
  App = "app"
}

export interface WithAuthState {
  view: View;
}

export enum AuthenticationMethod {
  ANONYMOUS = "ANONYMOUS",
  PASSWORD = "PASSWORD",
  ROLE_SWITCH = "ROLE_SWITCH",
  EXISTING_TOKEN = "EXISTING_TOKEN",
  GOOGLE = "GOOGLE",
  SAML2 = "SAML2",
  TRANSCARENT_SSO = "TRANSCARENT_SSO"
}

export interface Dependencies {
  props: Props;
  navigate: (path: string, options?: { replace: boolean }) => void;
  allSubscriptions?: SubscriptionDetailsResponse[];
  payerId?: string;
  sessionAuthenticationMethod?: string;
  authStage: AuthStage;
  authenticationStatus: AuthenticationStatus;
  credentialsRestored: boolean;
  sessionRestoreSettled: boolean;
  hasStoredCredentials: boolean;
  isBiometricEnabled?: boolean;
  isBiometricVerified?: boolean;
  requestedEmailLogin?: boolean;
  appAccessStatus?: AppAccessStatus;
}

export class WithAuthCubit extends Cubit<WithAuthState, Props> {
  componentProps!: Props;
  dependencies?: Dependencies;

  constructor() {
    super({
      view: View.Loading
    });
  }

  public readonly checkPassedAuthCheck = (): boolean => {
    const { hasActiveSubscription } = subscriptionState;

    if (this.dependencies?.authStage === "fullAuth") {
      return true;
    }

    if (
      this.dependencies?.authStage === "anonymous" &&
      this.componentProps.createAnonymousUser
    ) {
      return true;
    }

    // for funnel when user is allowed to have an email but not a password, or when the user has a subscription
    if (
      this.dependencies?.authStage === "partialAuth" &&
      (this.componentProps.allowPartialAuth || hasActiveSubscription)
    ) {
      return true;
    }

    return false;
  };

  /**
   * Returns 'pass' if the user has a transcarent payer and is authenticated with a valid sessionAuthenticationMethod
   * Returns 'fail' if the user has a transcarent payer and is not authenticated with a valid sessionAuthenticationMethod
   * Returns 'missing-data' if the payerId or sessionAuthenticationMethod is undefined
   */
  public readonly checkPassedTranscarentAuthCheck = (): "pass" | "fail" => {
    const { payerId, sessionAuthenticationMethod } = this.dependencies ?? {};

    const isTranscarentPayerId = payerId === PayerId.TRANSCARENT;
    if (!isTranscarentPayerId) {
      return "pass";
    }

    const isSamlAuthenticated =
      sessionAuthenticationMethod &&
      [
        AuthenticationMethod.SAML2,
        AuthenticationMethod.TRANSCARENT_SSO
      ].includes(sessionAuthenticationMethod as AuthenticationMethod);

    return isSamlAuthenticated ? "pass" : "fail";
  };

  public setView = (): void => {
    const {
      isBiometricEnabled,
      isBiometricVerified,
      authenticationStatus,
      credentialsRestored,
      sessionRestoreSettled,
      hasStoredCredentials,
      requestedEmailLogin
    } = this.dependencies ?? {};

    // PHASE 1: Biometric lock handling (before loading state)
    // Check email login fallback first (user requested to bypass biometrics)
    if (requestedEmailLogin) {
      this.patch({ view: View.Login });
      return;
    }

    // Show lock screen immediately if biometrics enabled and session not verified
    // This triggers biometric prompt before waiting for credential loading
    if (isBiometricEnabled && hasStoredCredentials && !isBiometricVerified) {
      this.patch({ view: View.Locked });
      return;
    }

    // PHASE 2: Loading states
    // The initial session restore gates the app. The storage read alone is not
    // enough for a device that has a session on it: it lands before authStage
    // does, and deciding from a stage that has not resolved yet flashes the
    // login overlay over the boot screen. The restore attempt itself is the
    // bound, so a failed one still lands on Login instead of waiting forever.
    // The network access check is not part of this - it resolves in the
    // background so a returning member never waits on it.
    const restoringSession =
      !credentialsRestored || (hasStoredCredentials && !sessionRestoreSettled);
    if (restoringSession) {
      this.patch({ view: View.Loading });
      return;
    }

    // PHASE 3: Authentication checks
    const passAuthCheck = this.checkPassedAuthCheck();

    // PHASE 3.5: App access check failed or timed out. Checked before both the
    // anonymous-user branch and the Login fallback below, because a failed
    // bootstrap must not be shown as a login screen or funneled into anonymous
    // account creation - only an actual missing session should be.
    // `hasStoredCredentials` only covers a session the device had before this
    // app instance booted, so a fresh sign-in needs the status check too.
    const hasSession =
      hasStoredCredentials || authenticationStatus === "authenticated";
    if (
      hasSession &&
      this.dependencies?.appAccessStatus === AppAccessStatus.UNKNOWN
    ) {
      this.patch({ view: View.Error });
      return;
    }

    // Handle anonymous user creation
    if (this.componentProps.createAnonymousUser && !passAuthCheck) {
      void authenticationState.createAnonymousAccount();
      this.patch({ view: View.Loading });
      return;
    }

    // Show login if not authenticated
    if (!passAuthCheck) {
      this.patch({ view: View.Login });
      return;
    }

    // PHASE 4: Provider-specific checks
    // Transcarent re-auth check (only when we have required data)
    const allRequiredDataAvailable = Boolean(
      this.dependencies?.sessionAuthenticationMethod
    );
    if (
      allRequiredDataAvailable &&
      this.checkPassedTranscarentAuthCheck() === "fail"
    ) {
      this.patch({ view: View.Reauth });
      return;
    }

    // PHASE 5: Authenticated - show app
    this.patch({ view: View.App });
  };

  initializeDependencies = (deps: Dependencies): void => {
    this.dependencies = deps;
    this.componentProps = deps.props;

    this.setView();
  };

  /**
   * Re-runs the app access check from View.Error. Shows Loading while in
   * flight; the result reaches this cubit through the usual dependencies
   * seam once AppAccessCubit emits its new status.
   */
  public readonly retry = (): void => {
    this.patch({ view: View.Loading });
    void this.reloadAccessCheck();
  };

  private readonly reloadAccessCheck = async (): Promise<void> => {
    const appAccessState = Blac.getBloc(AppAccessCubit);
    try {
      const outcome = await userState.loadAppAccess();
      await appAccessState.evaluateAccess(
        outcome.subscriptionsLoaded,
        outcome.profileLoaded
      );
      userState.loadDeferredUserData();
    } catch (error) {
      reportErrorSentry(error);
      await appAccessState.evaluateAccess(false);
    } finally {
      // A retry that fails again re-emits the status it already held, which does
      // not re-run WithAuth's effect, so the view would stay on Loading forever.
      // Re-read the settled status here and pick the view from it directly.
      if (this.dependencies) {
        this.dependencies.appAccessStatus = appAccessState.state.status;
        this.setView();
      }
    }
  };
}
